// "What changed since I last looked" (#3207), client side. Pure helpers plus the
// per-tab visit stamp; the hook that calls the API is hooks/useChanges.

export type ChangeKind = "new" | "bid" | "status";

export interface ChangesData {
  since: string;
  new: string[];
  bid: string[];
  status: string[];
  capped?: boolean;
}

/** Badge text per kind. A property with several changes shows the most useful one. */
export const CHANGE_LABELS: Record<ChangeKind, string> = {
  new: "New",
  bid: "Bid raised",
  status: "Status changed",
};

// Most useful first: a new bid on a live auction beats a status tweak, and
// "new" is the default reason a property is worth a look.
const PRIORITY: ChangeKind[] = ["bid", "new", "status"];

/** id -> every kind of change that property had, in priority order. */
export function changeKindsById(data: ChangesData | null): Map<string, ChangeKind[]> {
  const out = new Map<string, ChangeKind[]>();
  if (!data) return out;
  const add = (ids: string[] | undefined, kind: ChangeKind) => {
    for (const id of ids || []) {
      const list = out.get(id);
      if (list) list.push(kind);
      else out.set(id, [kind]);
    }
  };
  add(data.new, "new");
  add(data.bid, "bid");
  add(data.status, "status");
  out.forEach((kinds, id) => out.set(id, PRIORITY.filter((k) => kinds.includes(k))));
  return out;
}

/** Number of distinct properties with any change. */
export function changedCount(data: ChangesData | null): number {
  return changeKindsById(data).size;
}

// ── Visit stamp ──────────────────────────────────────────────────────────
//
// "Since I last looked" must survive a page reload inside the same visit, or
// every refresh would wipe the badges. So the comparison instant is fixed per
// browser TAB (sessionStorage), taken from the previous visit's stamp in
// localStorage; the stamp is then moved to now for the NEXT visit. A new tab
// or a later day is a new visit.

const LAST_KEY = "parcely.lastVisit";
const SESSION_KEY = "parcely.visitSince";

/** Default window for a first-ever visit: a week, so the badges mean something. */
export const FIRST_VISIT_LOOKBACK_MS = 7 * 24 * 60 * 60 * 1000;

export function resolveVisitSince(now: number = Date.now()): string {
  try {
    const fixed = window.sessionStorage.getItem(SESSION_KEY);
    if (fixed) return fixed;
    const last = window.localStorage.getItem(LAST_KEY);
    const since = last || new Date(now - FIRST_VISIT_LOOKBACK_MS).toISOString();
    window.sessionStorage.setItem(SESSION_KEY, since);
    window.localStorage.setItem(LAST_KEY, new Date(now).toISOString());
    return since;
  } catch {
    // Storage blocked: fall back to a week, recomputed each load.
    return new Date(now - FIRST_VISIT_LOOKBACK_MS).toISOString();
  }
}
